import { z } from 'zod';

// ============================================================================
// Core Entity Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  provider: 'google' | 'apple' | 'email';
  providerId: string;
  emailConfirmedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Goals System Types
// ============================================================================

export type GoalType =
  | 'retirement'
  | 'house_down_payment'
  | 'emergency_fund'
  | 'education'
  | 'vacation'
  | 'car'
  | 'wedding'
  | 'general_savings'
  | 'debt_payoff'
  | 'other';

export type GoalStatus =
  | 'planning'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

export type GoalPriority = 'low' | 'medium' | 'high' | 'critical';

export interface Goal {
  id: string;
  userId: string;

  // Goal details
  name: string;
  description?: string;
  goalType: GoalType;

  // Financial targets
  targetAmount: number;
  currentAmount: number;
  monthlyContribution?: number;

  // Timeline
  targetDate?: string;
  createdDate: string;

  // Goal management
  status: GoalStatus;
  priority: GoalPriority;

  // Progress tracking
  progressPercentage: number;
  lastContributionDate?: string;
  lastAnalysisAt?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface GoalMilestone {
  id: string;
  goalId: string;

  // Milestone details
  name: string;
  description?: string;
  targetAmount: number;
  targetPercentage: number;

  // Achievement tracking
  achieved: boolean;
  achievedAt?: string;
  achievedAmount?: number;

  // Milestone metadata
  isAutoGenerated: boolean;
  celebrationShown: boolean;

  createdAt: string;
  updatedAt: string;
}

export interface GoalCreateData {
  name: string;
  description?: string;
  goalType: GoalType;
  targetAmount: number;
  monthlyContribution?: number;
  targetDate?: string;
  priority?: GoalPriority;
}

export interface GoalUpdateData {
  name?: string;
  description?: string;
  targetAmount?: number;
  monthlyContribution?: number;
  targetDate?: string;
  priority?: GoalPriority;
  status?: GoalStatus;
}

export interface GoalSummary extends Goal {
  calculatedCurrentAmount: number;
  calculatedProgressPercentage: number;
  accountCount: number;
  daysRemaining?: number;
}

export interface GoalRecommendation {
  short: string;
  extended: string;
}

export interface GoalAnalysis {
  id: string;
  goalId: string;
  userId: string;

  // Analysis results
  analysis: string;
  recommendations: GoalRecommendation[];
  riskAssessment: 'low' | 'medium' | 'high';

  // Detailed scores
  feasibilityScore: number;
  riskScore: number;
  diversificationScore: number;
  costEfficiencyScore: number;
  goalAlignmentScore: number;
  compositeScore: number;

  // Analysis metadata
  analyzedAt: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Goal Questionnaire Types
// ============================================================================

export type QuestionType =
  | 'single_choice'
  | 'multiple_choice'
  | 'number_input'
  | 'text_input'
  | 'slider'
  | 'yes_no';

export interface QuestionOption {
  id: string;
  label: string;
  value: string | number;
  description?: string;
}

export interface GoalQuestion {
  id: string;
  goalType: GoalType;
  question: string;
  description?: string;
  type: QuestionType;
  required: boolean;
  options?: QuestionOption[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  dependsOn?: {
    questionId: string;
    value: string | number;
  };
}

export interface QuestionResponse {
  questionId: string;
  value: string | number | string[] | number[];
}

export interface GoalQuestionnaire {
  goalType: GoalType;
  questions: GoalQuestion[];
}

export interface GoalQuestionnaireResponse {
  goalId: string;
  responses: QuestionResponse[];
  completedAt: string;
}

export interface GoalStrategy {
  goalId: string;
  recommendedAllocation: {
    stocks: number;
    bonds: number;
    cash: number;
    alternatives?: number;
  };
  riskLevel: 'conservative' | 'moderate' | 'aggressive';
  timeHorizon: 'short' | 'medium' | 'long';
  recommendedContribution: number;
  strategy: string;
  reasoning: string;
  createdAt: string;
}

// User preferences and information
export interface UserPreferences {
  id: string;

  // Basic information
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;

  // Demographics for risk assessment
  ageGroup?:
    | 'under_18'
    | '18-25'
    | '26-35'
    | '36-45'
    | '46-55'
    | '56-65'
    | '65+';
  incomeRange?:
    | 'under_30k'
    | '30k_50k'
    | '50k_75k'
    | '75k_100k'
    | '100k_150k'
    | '150k_250k'
    | 'over_250k';
  employmentStatus?: string;
  dependents?: number;

  // Investment preferences
  investmentExperience?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  riskTolerance?:
    | 'conservative'
    | 'moderate'
    | 'aggressive'
    | 'very_aggressive';
  investmentTimelineYears?: number;

  // User preferences
  currency?: string;
  timezone?: string;
  notificationsEnabled?: boolean;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingProgress {
  id: string;
  userId: string;

  // Onboarding steps
  personalInfoCompleted: boolean;
  riskAssessmentCompleted: boolean;
  firstGoalCreated: boolean;
  firstAccountConnected: boolean;

  // Step completion timestamps
  personalInfoCompletedAt?: string;
  riskAssessmentCompletedAt?: string;
  firstGoalCreatedAt?: string;
  firstAccountConnectedAt?: string;

  // Progress metadata
  currentStep?: string;
  completionPercentage: number;

  createdAt: string;
  updatedAt: string;
}

export interface SimpleAccount {
  id: string;
  userId: string;
  accountType: 'brokerage' | 'ira' | 'roth_ira';
  accountName: string;
  plaidAccountId?: string;
  isManual: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Holding {
  id: string;
  accountId: string;
  symbol: string;
  quantity: number;
  averageCost?: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Accounts System Types
// ============================================================================

export type AccountType = 'plaid_account' | 'manual_portfolio';
export type AssetType =
  | 'stock'
  | 'etf'
  | 'mutual_fund'
  | 'bond'
  | 'fixed_income'
  | 'crypto'
  | 'cash'
  | 'real_estate'
  | 'commodity'
  | 'other';

// Asset type display labels for consistent UI presentation
export const assetTypeLabels: Record<AssetType, string> = {
  mutual_fund: 'Mutual Fund',
  stock: 'Stock',
  bond: 'Bond',
  fixed_income: 'Fixed Income',
  cash: 'Cash',
  etf: 'ETF',
  crypto: 'Cryptocurrency',
  real_estate: 'Real Estate',
  commodity: 'Commodity',
  other: 'Other',
};
export type TransactionType =
  | 'buy'
  | 'sell'
  | 'dividend'
  | 'deposit'
  | 'withdrawal'
  | 'fee'
  | 'transfer'
  | 'split'
  | 'other';

export interface Account {
  id: string;
  userId: string;
  goalId?: string | null;

  // Account identification
  name: string;
  description?: string;
  accountType: AccountType;

  // Reference to Plaid account data (only for plaid_account types)
  plaidAccountId?: string;

  // Plaid account type information (from accounts_with_plaid_details view)
  plaidAccountType?: string; // 'investment', 'credit', 'depository', 'loan', 'brokerage', 'other'
  plaidAccountSubtype?: string; // 'checking', 'savings', 'cd', 'money_market', 'ira', '401k', etc.

  // Cash information (for manual portfolios)
  cashAmount?: number;
  cashApy?: number;

  // Financial data
  currentBalance: number;
  currency: string;

  // Status
  isActive: boolean;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface AccountHolding {
  id: string;
  accountId: string;

  // Asset identification
  symbol: string;
  name?: string;
  assetType: AssetType;

  // Position details
  quantity: number;
  averageCost?: number;
  currentPrice?: number;
  marketValue?: number;

  // Performance
  unrealizedGainLoss?: number;
  unrealizedGainLossPercentage?: number;

  // Data sources
  priceSource?: string; // 'plaid', 'manual', 'api'
  lastPriceUpdate?: string;

  // Logo
  logoUrl?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface AggregatedHolding {
  date: string;
  plaid_account_id: string;
  ticker_symbol: string;
  total_quantity: number;
  avg_buy_price: number;
  closing_price: number;
  market_value: number;
  gain_loss: number;
  gain_loss_percent: number;
}

export interface AccountTransaction {
  id: string;
  accountId: string;
  holdingId?: string;

  // Transaction details
  transactionType: TransactionType;
  symbol?: string;
  quantity?: number;
  price?: number;
  amount: number;
  fee: number;

  // Transaction identification
  externalTransactionId?: string;
  plaidTransactionId?: string;

  // Dates and description
  transactionDate: string;
  description?: string;
  category?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface PlaidItem {
  id: string;
  userId: string;

  // Plaid identifiers
  plaidItemId: string;
  plaidAccessToken: string;

  // Institution details
  institutionId: string;
  institutionName: string;

  // Connection status
  lastSuccessfulSync?: string;
  lastSyncAttempt?: string;
  syncError?: string;

  // Webhook and cursor for updates
  webhookUrl?: string;
  cursor?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface AccountCreateData {
  name: string;
  description?: string;
  accountType: AccountType;
  goalId?: string;

  // Reference to Plaid account data (only for plaid_account types)
  plaidAccountId?: string;

  // Cash information (for manual portfolios)
  cashAmount?: number;
  cashApy?: number;

  // Initial balance
  currentBalance?: number;
  currency?: string;
}

export interface AccountUpdateData {
  name?: string;
  description?: string;
  goalId?: string | null;
  isActive?: boolean;

  // Cash information (for manual portfolios)
  cashAmount?: number;
  cashApy?: number;
}

export interface AccountHoldingCreateData {
  symbol: string;
  quantity: number;
  averageCost?: number;
}

export interface AccountHoldingUpdateData {
  quantity?: number;
  averageCost?: number;
}

export interface AccountSyncResult {
  accountId: string;
  success: boolean;
  lastSyncedAt: string;
  balanceUpdated: boolean;
  holdingsUpdated: number;
  transactionsInserted: number;
  transactionsUpdated: number;
  error?: string;
}

// ============================================================================
// Plaid Integration Types
// ============================================================================

export interface PlaidConnectionAccount {
  // API response fields (snake_case as returned by Edge Function)
  id: string;
  user_id: string;
  plaid_item_id: string;
  plaid_account_id: string;
  name: string;
  description: string | null;
  account_type: string; // Should be AccountType but API returns string
  account_subtype: string; // Plaid account subtype (checking, savings, brokerage, ira, etc.)
  institution_name: string;
  account_mask: string | null;
  current_balance: number;
  available_balance: number | null;
  currency: string;
  last_synced_at: string;
  sync_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlaidConnectionResponse {
  plaidItem: {
    id: string;
    userId: string;
    plaidItemId: string;
    institutionId: string;
    institutionName: string;
    createdAt: string;
    updatedAt: string;
  };
  accounts: PlaidConnectionAccount[];
  summary: {
    accountsCreated: number;
    accountsFailed: number;
    totalAccounts: number;
  };
}

// ============================================================================
// Authentication Types
// ============================================================================

export interface AuthSession {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface AuthProvider {
  id: 'google' | 'apple' | 'email';
  name: string;
  enabled: boolean;
}

export interface OAuthCredentials {
  provider: 'google' | 'apple';
  idToken: string;
  accessToken?: string;
}

export interface EmailCredentials {
  email: string;
  password: string;
}

export interface EmailSignupData {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface PasswordResetData {
  email: string;
}

export interface PasswordUpdateData {
  password: string;
  confirmPassword: string;
}

export interface AuthError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  errorType?: string;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  success: false;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: boolean;
}

// ============================================================================
// Form Data Types
// ============================================================================

export interface ProfileCreationData {
  name: string;
  riskTolerance: 'conservative' | 'moderate' | 'aggressive';
  investmentTimeline?: number;
  financialGoals: string[];
  brokerageInfo?: {
    brokerName?: string;
    accountNumber?: string;
    hasIRA?: boolean;
    hasRothIRA?: boolean;
  };
}

export interface HoldingEntryData {
  ticker: string;
  quantity: number;
  entryPrice?: number;
  entryDate?: string;
}

export interface AccountCreationData {
  accountType: 'brokerage' | 'ira' | 'roth_ira';
  accountName: string;
  isManual: boolean;
  plaidAccountId?: string;
}

// ============================================================================
// Security Information Types
// ============================================================================

export interface SecurityInfo {
  ticker: string;
  name: string;
  price?: number;
  currency?: string;
  exchange?: string;
  type: AssetType;
  logoUrl?: string;
}

export interface TickerValidationResult {
  isValid: boolean;
  security?: SecurityInfo;
  error?: string;
}

// ============================================================================
// Portfolio Analysis Types
// ============================================================================

export interface PortfolioSummary {
  totalValue: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  holdingsCount: number;
  lastUpdated: string;
}

export interface HoldingAnalysis extends Holding {
  currentPrice?: number;
  currentValue?: number;
  gainLoss?: number;
  gainLossPercent?: number;
  dailyChangePercent?: number;
  security?: SecurityInfo;
}

// ============================================================================
// Analytics and Reporting Types
// ============================================================================

export interface PerformanceDataPoint {
  date: string;
  value: number;
  gainLoss?: number;
  gainLossPercent?: number;
}

export interface PortfolioPerformance {
  timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';
  data: PerformanceDataPoint[];
  totalReturn: number;
  totalReturnPercent: number;
  startValue: number;
  endValue: number;
  startDate: string;
  endDate: string;
}

export interface AssetAllocation {
  ticker: string;
  name: string;
  value: number;
  percentage: number;
  sector?: string;
  type: AssetType;
  logoUrl?: string;
}

export interface SectorAllocation {
  sector: string;
  value: number;
  percentage: number;
  holdings: string[];
}

export interface BenchmarkComparison {
  portfolioReturn: number;
  benchmarkReturn: number;
  outperformance: number;
  benchmarkName: string;
  timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';
}

export interface PerformanceAlert {
  id: string;
  userId: string;
  type:
    | 'gain_threshold'
    | 'loss_threshold'
    | 'portfolio_value'
    | 'holding_change';
  condition: 'above' | 'below' | 'equals';
  threshold: number;
  currentValue: number;
  isTriggered: boolean;
  message: string;
  createdAt: string;
  triggeredAt?: string;
}

export interface ReportData {
  userId: string;
  userName: string;
  reportDate: string;
  timeRange: '1M' | '3M' | '6M' | '1Y' | 'ALL';
  portfolioSummary: PortfolioSummary;
  performance: PortfolioPerformance;
  assetAllocation: AssetAllocation[];
  sectorAllocation: SectorAllocation[];
  topPerformers: HoldingAnalysis[];
  bottomPerformers: HoldingAnalysis[];
  benchmarkComparison?: BenchmarkComparison;
}

export interface ExportOptions {
  format: 'pdf' | 'csv';
  timeRange: '1M' | '3M' | '6M' | '1Y' | 'ALL';
  includeCharts: boolean;
  includeHoldings: boolean;
  includePerformance: boolean;
}

// ============================================================================
// Marketplace/Ideas System Types
// ============================================================================

export interface IdeaAllocation {
  symbol: string;
  percentage: number;
  name?: string;
  logoUrl?: string;
  sector?: string;
  assetType?: AssetType;
}

export interface Idea {
  id: string;
  userId: string;
  creatorName?: string;
  creatorAvatar?: string;

  // Idea details
  name: string;
  description?: string;
  category?: string;
  accountId?: string; // Account that this idea is based on

  // Allocations (percentages only, no amounts)
  allocations: IdeaAllocation[];

  // Pricing
  priceType: 'free' | 'basic' | 'custom';
  monthlyPrice?: number; // Only for custom pricing

  // Public sharing
  visibility: 'public' | 'unlisted' | 'private';

  // Performance metrics (calculated)
  totalReturn?: number;
  totalReturnPercentage?: number;
  volatility?: number;
  sharpeRatio?: number;

  // Historical performance data
  performance?: IdeaPerformance[];

  // Engagement
  subscriberCount: number;
  viewCount: number;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export interface IdeaSubscription {
  id: string;
  userId: string;
  ideaId: string;
  subscribedAt: string;
  notificationsEnabled: boolean;
}

export interface IdeaPerformance {
  id: string;
  ideaId: string;
  dailyChangePercentage?: number;
  snapshotDate: string;
  createdAt: string;
}

export interface IdeaCreateData {
  name: string;
  description?: string;
  category?: string;
  accountId?: string;
  priceType: 'free' | 'basic' | 'custom';
  monthlyPrice?: number;
  visibility: 'public' | 'unlisted' | 'private';
}

export interface IdeaUpdateData {
  name?: string;
  description?: string;
  category?: string;
  priceType?: 'free' | 'basic' | 'custom';
  monthlyPrice?: number;
  visibility?: 'public' | 'unlisted' | 'private';
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const IdeaCreateSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Idea name is required')
      .max(255, 'Idea name too long'),
    description: z.string().max(1000, 'Description too long').optional(),
    category: z.string().max(100, 'Category too long').optional(),
    accountId: z.string().uuid('Account ID must be a valid UUID'),
    priceType: z.enum(['free', 'basic', 'custom'], {
      errorMap: () => ({ message: 'Please select a pricing option' }),
    }),
    monthlyPrice: z.number().positive('Price must be positive').optional(),
    visibility: z.enum(['public', 'unlisted', 'private'], {
      errorMap: () => ({ message: 'Please select a visibility option' }),
    }),
  })
  .refine(
    data => {
      if (data.priceType === 'custom' && !data.monthlyPrice) {
        return false;
      }
      return true;
    },
    {
      message: 'Monthly price is required for custom pricing',
      path: ['monthlyPrice'],
    }
  );

export const IdeaUpdateSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Idea name is required')
      .max(255, 'Idea name too long')
      .optional(),
    description: z.string().max(1000, 'Description too long').optional(),
    category: z.string().max(100, 'Category too long').optional(),
    priceType: z.enum(['free', 'basic', 'custom']).optional(),
    monthlyPrice: z.number().positive('Price must be positive').optional(),
    visibility: z.enum(['public', 'unlisted', 'private']).optional(),
  })
  .refine(
    data => {
      if (data.priceType === 'custom' && data.monthlyPrice === undefined) {
        return false;
      }
      return true;
    },
    {
      message: 'Monthly price is required for custom pricing',
      path: ['monthlyPrice'],
    }
  );

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  provider: z.enum(['google', 'apple', 'email']),
  providerId: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const AccountSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  accountType: z.enum(['brokerage', 'ira', 'roth_ira']),
  accountName: z
    .string()
    .min(1, 'Account name is required')
    .max(255, 'Account name too long'),
  plaidAccountId: z.string().optional(),
  isManual: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const HoldingSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  symbol: z
    .string()
    .min(1, 'Symbol is required')
    .max(10, 'Symbol too long')
    .regex(/^[A-Z]+$/, 'Symbol must be uppercase letters only'),
  quantity: z.number().positive('Quantity must be positive'),
  averageCost: z.number().positive().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Form validation schemas
export const ProfileCreationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name too long'),
  riskTolerance: z.enum(['conservative', 'moderate', 'aggressive'], {
    errorMap: () => ({ message: 'Please select a risk tolerance' }),
  }),
  investmentTimeline: z
    .number()
    .int()
    .min(1, 'Timeline must be at least 1 year')
    .max(50, 'Timeline cannot exceed 50 years')
    .optional(),
  financialGoals: z
    .array(z.string())
    .min(1, 'Please select at least one financial goal'),
  brokerageInfo: z
    .object({
      brokerName: z.string().optional(),
      accountNumber: z.string().optional(),
      hasIRA: z.boolean().optional(),
      hasRothIRA: z.boolean().optional(),
    })
    .optional(),
});

export const HoldingEntrySchema = z.object({
  ticker: z
    .string()
    .min(1, 'Ticker is required')
    .max(10, 'Ticker too long')
    .regex(/^[A-Za-z]+$/, 'Ticker must contain only letters')
    .transform(val => val.toUpperCase()),
  quantity: z.number().positive('Quantity must be positive'),
  entryPrice: z.number().positive('Entry price must be positive').optional(),
  entryDate: z.string().date().optional(),
});

export const AccountCreationSchema = z.object({
  accountType: z.enum(['brokerage', 'ira', 'roth_ira'], {
    errorMap: () => ({ message: 'Please select an account type' }),
  }),
  accountName: z
    .string()
    .min(1, 'Account name is required')
    .max(255, 'Account name too long'),
  isManual: z.boolean(),
  plaidAccountId: z.string().optional(),
});

// Email authentication validation schemas
export const EmailCredentialsSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export const EmailSignupSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      ),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const PasswordResetSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

export const PasswordUpdateSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      ),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Goals validation schemas
export const GoalCreateSchema = z.object({
  name: z
    .string()
    .min(1, 'Goal name is required')
    .max(255, 'Goal name too long'),
  description: z.string().max(1000, 'Description too long').optional(),
  goalType: z.enum(
    [
      'retirement',
      'house_down_payment',
      'emergency_fund',
      'education',
      'vacation',
      'car',
      'wedding',
      'general_savings',
      'debt_payoff',
      'other',
    ],
    {
      errorMap: () => ({ message: 'Please select a valid goal type' }),
    }
  ),
  targetAmount: z.number().positive('Target amount must be positive'),
  monthlyContribution: z
    .number()
    .positive('Monthly contribution must be positive')
    .optional(),
  targetDate: z.string().date().optional(),
  priority: z.enum(['low', 'medium', 'high', 'critical']).optional(),
});

export const GoalUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Goal name is required')
    .max(255, 'Goal name too long')
    .optional(),
  description: z.string().max(1000, 'Description too long').optional(),
  targetAmount: z
    .number()
    .positive('Target amount must be positive')
    .optional(),
  monthlyContribution: z
    .number()
    .positive('Monthly contribution must be positive')
    .optional(),
  targetDate: z.string().date().optional(),
  priority: z.enum(['low', 'medium', 'high', 'critical']).optional(),
  status: z
    .enum(['planning', 'active', 'paused', 'completed', 'cancelled'])
    .optional(),
});

// Account validation schemas
export const AccountCreateSchema = z.object({
  name: z
    .string()
    .min(1, 'Account name is required')
    .max(255, 'Account name too long'),
  description: z.string().max(1000, 'Description too long').optional(),
  accountType: z.enum(['plaid_account', 'manual_portfolio'], {
    errorMap: () => ({ message: 'Please select a valid account type' }),
  }),
  goalId: z.string().uuid().optional(),

  // Reference to Plaid account data (only for plaid_account types)
  plaidAccountId: z.string().optional(),

  // Cash information (for manual portfolios)
  cashAmount: z
    .number()
    .nonnegative('Cash amount cannot be negative')
    .optional(),
  cashApy: z.number().min(0).max(100, 'APY cannot exceed 100%').optional(),

  // Initial balance
  currentBalance: z
    .number()
    .nonnegative('Balance cannot be negative')
    .optional(),
  currency: z.string().length(3, 'Currency must be 3 characters').optional(),
});

export const AccountUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Account name is required')
    .max(255, 'Account name too long')
    .optional(),
  description: z.string().max(1000, 'Description too long').optional(),
  goalId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),

  // Cash information (for manual portfolios)
  cashAmount: z
    .number()
    .nonnegative('Cash amount cannot be negative')
    .optional(),
  cashApy: z.number().min(0).max(100, 'APY cannot exceed 100%').optional(),
});

export const AccountHoldingCreateSchema = z.object({
  symbol: z
    .string()
    .min(1, 'Symbol is required')
    .max(10, 'Symbol too long')
    .regex(/^[A-Za-z]+$/, 'Symbol must contain only letters')
    .transform(val => val.toUpperCase()),
  quantity: z.number().positive('Quantity must be positive'),
  averageCost: z.number().positive('Average cost must be positive').optional(),
});

export const AccountHoldingUpdateSchema = z.object({
  quantity: z.number().positive('Quantity must be positive').optional(),
  averageCost: z.number().positive('Average cost must be positive').optional(),
});

// ============================================================================
// Account Performance Types
// ============================================================================

export interface AccountPerformance {
  id: string;
  accountId: string;

  // Simplified: only total value
  totalValue: number;

  // Daily changes
  dailyChange?: number;
  dailyChangePercentage?: number;

  // Snapshot metadata
  snapshotDate: string;

  createdAt: string;
}

// Type inference from schemas
export type UserValidation = z.infer<typeof UserSchema>;

export type AccountValidation = z.infer<typeof AccountSchema>;
export type HoldingValidation = z.infer<typeof HoldingSchema>;

export type HoldingEntryValidation = z.infer<typeof HoldingEntrySchema>;
export type AccountCreationValidation = z.infer<typeof AccountCreationSchema>;
export type EmailCredentialsValidation = z.infer<typeof EmailCredentialsSchema>;
export type EmailSignupValidation = z.infer<typeof EmailSignupSchema>;
export type PasswordResetValidation = z.infer<typeof PasswordResetSchema>;
export type PasswordUpdateValidation = z.infer<typeof PasswordUpdateSchema>;
export type GoalCreateValidation = z.infer<typeof GoalCreateSchema>;
export type GoalUpdateValidation = z.infer<typeof GoalUpdateSchema>;
export type AccountCreateValidation = z.infer<typeof AccountCreateSchema>;
export type AccountUpdateValidation = z.infer<typeof AccountUpdateSchema>;
export type AccountHoldingCreateValidation = z.infer<
  typeof AccountHoldingCreateSchema
>;
export type AccountHoldingUpdateValidation = z.infer<
  typeof AccountHoldingUpdateSchema
>;
